import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { InfoTooltip } from '@components/common/InfoTooltip';
import { useTranslation } from 'react-i18next';
import { dailyNormCard, dayNutrientBox } from '@/styles/tokens';
import { DailyNormsData } from '@/types/settings.types';
import { DayBalanceView } from './dayBalance';
import { DayNormProgress } from '../day-norm-progress/dayNormProgress';

interface DayNutrientBoxesProps {
  balance: DayBalanceView;
  progress: DayNormProgress | null;
  /** Для тексту пояснення в тултіпі ("Денна норма: X") - ті самі цілі, з яких рахується progress. */
  dailyNorms: DailyNormsData | null;
  /** Того дня діяли власні норми експерта - одна крапка на клітинку дня (правий верхній кут, біля дати). */
  own?: boolean;
  /** Вузька колонка (подання "2 тижні") - лише картка Ккал. */
  compact?: boolean;
}

const BOXES = [
  { key: 'protein', label: 'Б:', title: 'Білки', unit: 'г', goalField: 'proteinG' },
  { key: 'fat', label: 'Ж:', title: 'Жири', unit: 'г', goalField: 'fatG' },
  { key: 'carbs', label: 'В:', title: 'Вуглеводи', unit: 'г', goalField: 'carbsG' },
  { key: 'kcal', label: 'Ккал', title: 'Калорії', unit: 'ккал', goalField: 'caloriesIntake' },
] as const;

const TEXT_SX = { fontSize: 9, lineHeight: '11px', fontFamily: 'Roboto, sans-serif' } as const;

function describeBox(value: number | null, percent: number | null, goal: number | null, unit: string): string {
  if (value == null || percent == null || goal == null) return 'Немає даних.';
  return `Заплановано ${value} ${unit} — ${percent}% від денного плану. Денна норма: ${goal} ${unit}.`;
}

const NutrientBox: React.FC<{
  label: string; title: string; unit: string; value: number | null; percent: number | null; goal: number | null;
}> = ({ label, title, unit, value, percent, goal }) => (
  <InfoTooltip title={title} description={describeBox(value, percent, goal, unit)}>
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        p: '4px',
        gap: '2px',
        width: 29,
        height: 45,
        bgcolor: dayNutrientBox.background,
        borderRadius: '6px',
        flex: 1,
        cursor: 'default',
      }}
    >
      <Typography sx={{ ...TEXT_SX, fontWeight: 500, color: dayNutrientBox.text }}>{label}</Typography>
      <Typography sx={{ ...TEXT_SX, fontWeight: 500, color: dayNutrientBox.text }}>{value ?? '—'}</Typography>
      <Typography sx={{ ...TEXT_SX, fontWeight: 400, color: dayNutrientBox.textMuted }}>
        {percent != null ? `${percent}%` : '—'}
      </Typography>
    </Box>
  </InfoTooltip>
);

// Підказка крапки власних норм: самі значення того дня (норми дня = версія власних норм експерта).
const OwnNormsSummary: React.FC<{ norms: DailyNormsData | null }> = ({ norms }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'clientProfile.dailyNorms' });
  const value = (v: number | null | undefined, unit?: string) => (v == null ? '—' : unit ? `${v} ${unit}` : String(v));
  return (
    <Box sx={{ fontSize: 12, lineHeight: '18px' }}>
      <Box sx={{ fontWeight: 600 }}>{t('ownTitle')}</Box>
      <Box>
        {t('norm.calories')} {value(norms?.caloriesIntake, t('unit.kcal'))} · {t('norm.water')} {value(norms?.waterMl, t('unit.ml'))}
      </Box>
      <Box>
        {t('short.protein')} {value(norms?.proteinG)} · {t('short.fat')} {value(norms?.fatG)} · {t('short.carbs')} {value(norms?.carbsG)} {t('unit.g')}
      </Box>
    </Box>
  );
};

/**
 * Підсумок дня в заголовку колонки календаря - ряд карток Б/Ж/В/Ккал (значення спожитого + % від
 * денної цілі в одній картці), під смугою прогресу виконання задач (Figma-макет, 2026-10-15).
 * Замінює колишні окремі рядки DayBalanceSummary (лише значення) і DayNormProgressRow (лише %).
 * Під ним - рядок витрати енергії: така сама картка під карткою Ккал (значення + % від денної цілі
 * витрати); немає доступу до тренувань (burned = null) - рядка нема.
 *
 * Тултіп на кожній картці (InfoTooltip, той самий Figma-макет) пояснює число: заплановано/% від
 * плану/денна норма - "Немає даних." якщо чогось із трійки (значення/%/ціль) порахувати не вдалось.
 */
export const DayNutrientBoxes: React.FC<DayNutrientBoxesProps> = ({ balance, progress, dailyNorms, own = false, compact = false }) => {
  const items = compact ? BOXES.filter(b => b.key === 'kcal') : BOXES;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', p: '0 4px 4px', gap: '2px' }}>
      {own && (
        // Позиціюється від клітинки дня в шапці календаря (sticky <th>) - правий верхній кут, біля дати.
        <Tooltip title={<OwnNormsSummary norms={dailyNorms} />}>
          <Box sx={{ position: 'absolute', top: 4, right: 4, width: 6, height: 6, borderRadius: '50%', bgcolor: dailyNormCard.ownMarker }} />
        </Tooltip>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '2px' }}>
        {items.map(({ key, label, title, unit, goalField }) => (
          <NutrientBox
            key={key}
            label={label}
            title={title}
            unit={unit}
            value={balance[key]}
            percent={progress?.[key] ?? null}
            goal={dailyNorms?.[goalField] ?? null}
          />
        ))}
      </Box>
      {/* Рядок витрати енергії тимчасово приховано.
      {balance.burned != null && (
        // Під карткою Ккал: порожні місця під Б/Ж/В тримають ту саму сітку.
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '2px' }}>
          {items.slice(0, -1).map(({ key }) => <Box key={key} sx={{ flex: 1, width: 29 }} />)}
          <NutrientBox
            label="Витр."
            title="Витрата енергії"
            unit="ккал"
            value={balance.burned}
            percent={progress?.burned ?? null}
            goal={dailyNorms?.caloriesBurned ?? null}
          />
        </Box>
      )}
      */}
    </Box>
  );
};
